import React from "react";

import { useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import ojieIcon from "@/assets/icons/ojie.png";
import { graphql } from "@/gql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";

import RatingIcon from "./RatingIcon";

type Props = {
  visible: boolean;
  onClose: () => void;
};

const useStyles = makeStyles()((theme) => ({
  dialog: {
    position: "absolute",
    bottom: 0,
    right: 0,
    height: "initial",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 4px",
    backgroundColor: "#FFFAEF",
  },
  headerText: {
    fontWeight: "bold",
  },
  body: {
    padding: 20,
  },
  ratingLabel: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 12,
  },
  feedbackField: {
    display: "flex",
    flexDirection: "column",
    marginTop: 12,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  ratingButtonGroup: {
    display: "flex",
    justifyContent: "space-between",
  },
  ratingButton: {
    padding: 0,
    "&.MuiToggleButton-root": {
      border: 0,
      borderRadius: 9999,
    },
  },
}));

const validationSchema = yup
  .object({
    rating: yup.number().presence().required("Please choose a rating"),
    reason: yup.string(),
  })
  .defined();

const createNpsFeedbackMutation = graphql(`
  mutation CreateNpsFeedback($input: CreateNpsFeedbackInput!) {
    createNpsFeedback(input: $input) {
      npsFeedback {
        id
        coachId
        reason
        rating
      }
      errors {
        fullMessage
      }
    }
  }
`);

const FeedbackDialog: React.FC<Props> = ({ visible, onClose }) => {
  const [createAppFeedback] = useMutation(createNpsFeedbackMutation);
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const ratingOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useFormWithYup({
    defaultValues: {
      rating: "",
      reason: "",
    },
    mode: "onChange",
    validationSchema,
  });

  async function onSubmit(values: yup.InferType<typeof validationSchema>) {
    const input = {
      rating: values.rating ?? 0,
      reason: values.reason,
    };
    try {
      const result = await createAppFeedback({
        variables: { input: input },
      });
      onClose();

      if (result.data?.createNpsFeedback?.errors) {
        snackbar.open(result.data.createNpsFeedback.errors[0].fullMessage);
      } else {
        snackbar.open("Thank you for your feedback!");
      }
    } catch (e: any) {
      snackbar.open(e.message);
    }
  }

  return (
    <Dialog
      classes={{ container: classes.dialog }}
      open={visible}
      onClose={onClose}
    >
      <DialogTitle className={classes.header}>
        <img src={ojieIcon} alt="" />
        <Typography variant="inherit" className={classes.headerText}>
          Would you recommend NutriCoach to your friend or colleagues?
        </Typography>
        <Box alignSelf="flex-start">
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.body}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className={classes.ratingLabel}>
            <Typography variant="caption" color="textSecondary">
              I&apos;ll not recommend
            </Typography>
            <Typography variant="caption" color="textSecondary">
              I&apos;ll highly recommend
            </Typography>
          </Box>
          <Controller
            control={control}
            name="rating"
            render={({ field: { value, onChange } }) => (
              <ToggleButtonGroup
                className={classes.ratingButtonGroup}
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                exclusive
              >
                {ratingOptions.map((rating) => (
                  <ToggleButton
                    className={classes.ratingButton}
                    key={rating}
                    aria-label={`rating ${rating}`}
                    size="small"
                    value={rating}
                  >
                    <RatingIcon rating={rating} />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          />
          {!!errors.rating && (
            <FormHelperText error>{errors.rating?.message}</FormHelperText>
          )}
          <Box className={classes.feedbackField}>
            <Typography variant="h6">
              What is the most important reason for your score?
            </Typography>
            <TextField {...register("reason")} multiline rows={2} fullWidth />
          </Box>
          <Box marginTop={4} marginX="auto" width="fit-content">
            <Button type="submit" disabled={!isValid || isSubmitting}>
              Submit
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
